import {
  Button,
  Card,
  CircularProgress,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  editAcademicYearsList,
  getAcademicYearsList,
  postAcademicYearsList,
} from '../../features/history/HistorySlice';
import EditIcon from '@mui/icons-material/Edit';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import ToastNotification from '../UIElements/ToastNotification';
import useNotification from '../../hooks/useNotification';
function AcademicYears() {
  const dispatch = useDispatch();

  const [academicYearsList, setAcademicYearsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState({
    code: '',
    academicStart: '',
    academicEnd: '',
  });
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();

  async function getAcademicYears() {
    setLoading(true);
    try {
      const response = await dispatch(getAcademicYearsList()).unwrap();
      if (response.status === 200) {
        setAcademicYearsList(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    getAcademicYears();
  }, []);
  return (
    <>
      <Card className='p-4 border-t max-w-5xl pb-5'>
        <Typography variant='h6' color='primary' className='mb-1'>
          Manage Academic Years
        </Typography>
        {/* Form for Adding of the Academic Year */}
        <Formik
          initialValues={details}
          validationSchema={Yup.object({
            code: Yup.string().required('Academic Code is required'),
            academicStart: Yup.date().required('Enter Academic  Year Start'),
            academicEnd: Yup.date()
              .required('Enter Academic  Year End')
              .min(
                Yup.ref('academicStart'),
                'Academic Year End  cannot be before the Academic Year Start',
              )
              .test(
                'is-after',
                'End date must be after start date',
                function (value) {
                  const { academicStart } = this.parent;
                  if (!value || !academicStart) return true;

                  // Comparison: End Date must be strictly greater than Start Date
                  return (
                    new Date(value).getTime() >
                    new Date(academicStart).getTime()
                  );
                },
              ),
          })}
          enableReinitialize={true}
          onSubmit={async (values, { resetForm }) => {
            setLoading(true);
            if (values?.id) {
              try {
                const response = await dispatch(
                  editAcademicYearsList(values),
                ).unwrap();
                if (response?.status === 200) {
                  showSuccess(' Academic Detials modified Sucessfully');
                  getAcademicYears();
                  resetForm();
                } else {
                  showError('Error in Modifiying  Academic Detials');
                }
              } catch (error) {
                if (error.status == 409)
                  showError(' Academic dates overlapped with previous dates');
                else showError('Error in Creating Academic Details');
              } finally {
                setLoading(false);
              }
            } else {
              try {
                const response = await dispatch(
                  postAcademicYearsList(values),
                ).unwrap();
                if (response?.status === 200) {
                  showSuccess('Academic Details Created Sucessfully');
                  resetForm();
                  getAcademicYears();
                }
              } catch (error) {
                if (error.status == 409)
                  showError(' Academic dates overlapped with previous dates');
                else showError('Error in Creating Academic Details');
              } finally {
                setLoading(false);
              }
            }
            setDetails({
              code: '',
              academicStart: '',
              academicEnd: '',
            });
            getAcademicYearsList();
          }}
        >
          {() => (
            <>
              <Form>
                <Card className='p-4  max-w-3xl border'>
                  <div className='max-w-md flex flex-col gap-2 mt-1'>
                    <div className='flex gap-4 items-center justify-between flex-wrap'>
                      <InputLabel for='academicyearcode'>
                        Academic Year Code
                      </InputLabel>
                      <Field name='code'>
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            size='small'
                            id='academicyearcode'
                            className='w-52'
                            placeholder='Eg:2026-2027'
                            onChange={field.onChange}
                            helperText={meta.touched && meta.error}
                            error={meta.touched && Boolean(meta.error)}
                            InputLabelProps={{ shrink: true }}
                          ></TextField>
                        )}
                      </Field>
                    </div>
                    <div className='flex gap-4 items-center justify-between flex-wrap'>
                      <InputLabel for='academicStart'>
                        Academic Year Start
                      </InputLabel>
                      <Field name='academicStart'>
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            type='date'
                            size='small'
                            id='academicStart'
                            className='w-52'
                            onChange={field.onChange}
                            helperText={meta.touched && meta.error}
                            error={meta.touched && Boolean(meta.error)}
                            InputLabelProps={{ shrink: true }}
                          ></TextField>
                        )}
                      </Field>
                    </div>
                    <div className='flex gap-4 items-center justify-between flex-wrap'>
                      <InputLabel for='academicEnd'>
                        Academic Year End
                      </InputLabel>
                      <Field name='academicEnd'>
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            type='date'
                            size='small'
                            id='academicEnd'
                            className='w-52'
                            onChange={field.onChange}
                            helperText={meta.touched && meta.error}
                            error={meta.touched && Boolean(meta.error)}
                            InputLabelProps={{ shrink: true }}
                          ></TextField>
                        )}
                      </Field>
                    </div>
                    <div className='flex items-center justify-end gap-4 flex-wrap mt-4'>
                      <Button
                        color='error'
                        variant='outlined'
                        type='reset'
                        onClick={() =>
                          setDetails({
                            code: '',
                            academicStart: '',
                            academicEnd: '',
                          })
                        }
                      >
                        Clear
                      </Button>
                      <Button type='submit' variant='contained'>
                        Submit
                      </Button>
                    </div>
                  </div>
                </Card>
              </Form>
              <ToastNotification
                open={notification.open}
                onClose={hideNotification}
                message={notification.message}
                title={notification.title}
                severity={notification.severity}
                autoHideDuration={notification.autoHideDuration}
                position={notification.position}
              />
            </>
          )}
        </Formik>

        <Typography variant='h6' color='primary' className='mt-4 mb-1'>
          Academic Years Detials
        </Typography>
        {/* Loading icon */}
        {academicYearsList.length === 0 && loading && (
          <Card className='min-h-44 flex items-center justify-center gap-4 max-w-2xl border-t'>
            <CircularProgress></CircularProgress>{' '}
            <Typography variant='body1'>
              fetching the Academic Years data...
            </Typography>
          </Card>
        )}
        {/* Not Data Meassage */}
        {academicYearsList.length === 0 && !loading && (
          <Card className='min-h-44 flex items-center justify-center gap-4 max-w-2xl border-t'>
            <Typography variant='body1'>
              No previous academic years data avialble
            </Typography>
          </Card>
        )}
        {/* Previous Years Academic Data Table order-Wise */}
        {academicYearsList.length > 0 && (
          <Card className='overflow-x-auto max-w-3xl'>
            {' '}
            <TableContainer
              component={Paper}
              variant='outlined'
              sx={{
                maxHeight: 600,
                overflow: 'auto',
              }}
            >
              <Table stickyHeader size='small' sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell className='text-blue-600'>
                      <strong>SNO</strong>
                    </TableCell>
                    <TableCell className='text-blue-600'>
                      <strong>Academic Year Code</strong>
                    </TableCell>
                    <TableCell className='text-blue-600'>
                      <strong>Academic Year Start</strong>
                    </TableCell>
                    <TableCell className='text-blue-600'>
                      <strong>Academic Year End</strong>
                    </TableCell>
                    <TableCell className='text-blue-600'>
                      <strong>Available for Migration</strong>
                    </TableCell>
                    <TableCell className='text-blue-600'>
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {academicYearsList?.map((obj, index) => (
                    <TableRow
                      key={obj?.id}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: '#fafafa',
                        },
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>

                      <TableCell className='truncate max-w-[400px] '>
                        {obj.code}
                      </TableCell>
                      <TableCell className='truncate max-w-[400px] '>
                        {obj.academicStart?.split('-')?.reverse()?.join('-')}
                      </TableCell>
                      <TableCell className='truncate max-w-[400px] '>
                        {obj.academicEnd?.split('-')?.reverse()?.join('-')}
                      </TableCell>
                      <TableCell className='truncate max-w-[400px] '>
                        {obj.status ? <div>NO</div> : <div>YES</div>}
                      </TableCell>
                      <TableCell align='center' className='flex gap-2'>
                        <EditIcon
                          className='text-blue-400 cursor-pointer text-center'
                          onClick={() => {
                            setDetails({ ...obj });
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>{' '}
          </Card>
        )}
      </Card>
    </>
  );
}
export default AcademicYears;
